import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { capabilityTooltip, hasFrontendPermission } from '../permissions';
import { useToast } from '../components/ui/ToastProvider';

const DEFAULT_DISPATCH_SETTINGS = Object.freeze({
  maxJobsPerTechnicianPerDay: 2,
  slaDueSoonDays: 1,
});

const normalizeDispatchSettings = (value = {}) => {
  const maxJobs = Number(value.maxJobsPerTechnicianPerDay);
  const dueSoonDays = Number(value.slaDueSoonDays);
  return {
    maxJobsPerTechnicianPerDay: Number.isFinite(maxJobs) ? Math.max(1, Math.min(20, Math.floor(maxJobs))) : DEFAULT_DISPATCH_SETTINGS.maxJobsPerTechnicianPerDay,
    slaDueSoonDays: Number.isFinite(dueSoonDays) ? Math.max(0, Math.min(14, Math.floor(dueSoonDays))) : DEFAULT_DISPATCH_SETTINGS.slaDueSoonDays,
  };
};

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function SettingsStat({ label, value, tone = 'default' }) {
  return (
    <article className={`settings-stat settings-stat--${tone}`}>
      <span className="settings-stat-label">{label}</span>
      <strong className="settings-stat-value">{value}</strong>
    </article>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const auth = loadStoredAuth();
  const token = auth?.token;
  const user = auth?.user;
  const canManageDispatch = hasFrontendPermission(user, 'dispatch.manage');

  const [capabilities, setCapabilities] = useState(null);
  const [dispatchSettings, setDispatchSettings] = useState(DEFAULT_DISPATCH_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const loadSettings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [capsData, settingsData] = await Promise.all([
        apiFetch('/api/auth/capabilities', { token }),
        apiFetch('/api/settings/dispatch', { token }),
      ]);
      setCapabilities(capsData || null);
      setDispatchSettings(normalizeDispatchSettings(settingsData || {}));
    } catch (e) {
      setError(e.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings, refreshKey]);

  const stats = useMemo(() => ({
    role: capabilities?.role || user?.role || 'unknown',
    permissions: Array.isArray(capabilities?.permissions) ? capabilities.permissions.length : 0,
    maxJobs: dispatchSettings.maxJobsPerTechnicianPerDay,
    dueSoon: dispatchSettings.slaDueSoonDays,
  }), [capabilities, dispatchSettings, user?.role]);

  const handleSaveDispatch = async (event) => {
    event.preventDefault();
    if (!token || !canManageDispatch) return;
    setSaving(true);
    setError('');
    try {
      const payload = normalizeDispatchSettings(dispatchSettings);
      const saved = await apiFetch('/api/settings/dispatch', {
        token,
        method: 'PUT',
        body: payload,
      });
      setDispatchSettings(normalizeDispatchSettings(saved || payload));
      pushToast('Dispatch settings saved', 'success');
    } catch (e) {
      setError(e.message || 'Failed to save dispatch settings');
      pushToast(e.message || 'Failed to save dispatch settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="settings-page">
        <Skeleton lines={10} />
      </section>
    );
  }

  return (
    <section className="settings-page">
      <div className="settings-header">
        <div>
          <span className="settings-eyebrow">Control</span>
          <h1 className="settings-heading">Settings</h1>
          <p className="settings-subheading">
            Dispatch rules, role capabilities, and current account-level access context.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setRefreshKey((value) => value + 1)}>
          <RefreshIcon />
          Refresh
        </Button>
      </div>

      {error ? <ErrorState message={error} onRetry={loadSettings} /> : null}

      <div className="settings-stats-grid">
        <SettingsStat label="Role" value={String(stats.role).toUpperCase()} />
        <SettingsStat label="Capabilities" value={stats.permissions} tone="accent" />
        <SettingsStat label="Max Jobs / Tech" value={stats.maxJobs} tone="info" />
        <SettingsStat label="Due Soon Window" value={`${stats.dueSoon}d`} tone="warning" />
      </div>

      <div className="settings-layout">
        <Card title="Dispatch Rules">
          <form className="settings-form" onSubmit={handleSaveDispatch}>
            <label className="settings-field">
              <span className="settings-field-label">Max Jobs Per Technician Per Day</span>
              <input
                className="settings-input"
                type="number"
                min="1"
                max="20"
                value={dispatchSettings.maxJobsPerTechnicianPerDay}
                onChange={(event) => setDispatchSettings((prev) => ({ ...prev, maxJobsPerTechnicianPerDay: event.target.value }))}
                disabled={!canManageDispatch}
              />
            </label>
            <label className="settings-field">
              <span className="settings-field-label">SLA Due Soon Window</span>
              <input
                className="settings-input"
                type="number"
                min="0"
                max="14"
                value={dispatchSettings.slaDueSoonDays}
                onChange={(event) => setDispatchSettings((prev) => ({ ...prev, slaDueSoonDays: event.target.value }))}
                disabled={!canManageDispatch}
              />
            </label>
            <div className="settings-actions">
              <Button
                type="submit"
                disabled={!canManageDispatch || saving}
                title={!canManageDispatch ? capabilityTooltip('dispatch.manage') : 'Save dispatch rules'}
              >
                Save Rules
              </Button>
            </div>
          </form>
        </Card>

        <Card title="Capability Snapshot">
          {capabilities?.permissions?.length ? (
            <div className="settings-capability-list">
              {capabilities.permissions.map((permission) => (
                <span className="settings-capability-chip" key={permission}>{permission}</span>
              ))}
            </div>
          ) : (
            <EmptyState title="No capabilities loaded" message="Capability metadata is unavailable for this session." />
          )}
        </Card>
      </div>
    </section>
  );
}
